/* eslint-disable @typescript-eslint/no-explicit-any */
import { createStore } from 'idb-keyval';

const DB_NAME = 'cx_operation_system';
const DB_VERSION = 2; // 新增表就把版本号+1！非常关键

let dbReadyPromise: Promise<void> | null = null;

/**
 * 强制创建数据库 + 所有需要的表
 * 初始化时调用一次，App入口最先await这个函数！
 */
export async function initIndexedDB(): Promise<void> {
    if (dbReadyPromise) return dbReadyPromise;
    dbReadyPromise = new Promise((resolve, reject) => {
        if (!window.indexedDB) {
            reject(new Error('浏览器不支持 IndexedDB'));
            return;
        }
        // ✅ 指定版本号！！！
        const request = window.indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (e) => {
            const db = (e.target as IDBOpenDBRequest).result;
            const stores = ['param-store', 'columns-store', 'screen-store', 'temporary-store', 'plugin-store'];
            stores.forEach(name => {
                if (!db.objectStoreNames.contains(name)) {
                    db.createObjectStore(name);
                    console.log('✅ 表创建：', name);
                }
            })
        };
        request.onsuccess = () => {
            request.result.close();
            resolve();
        };
        request.onerror = () => reject(request.error);
    });
    return dbReadyPromise;
}

/**
 * 获取指定表的操作实例
 * @param storeName
 */
export function getDB(storeName: string) {
    const store = createStore(DB_NAME, storeName);
    return {
        async setItem(key: string, value: any) {
            // ✅ 读写前等待初始化完成
            await initIndexedDB();
            return await import('idb-keyval').then((m) => m.set(key, JSON.stringify(value), store));
        },
        async getItem(key: string) {
            await initIndexedDB();
            try {
                const val = await import('idb-keyval').then((m) => m.get(key, store));
                return val ? JSON.parse(val) : null;
            } catch {
                return null;
            }
        },
        async removeItem(key: string) {
            await initIndexedDB();
            return await import('idb-keyval').then((m) => m.del(key, store));
        },
        async clearAll() {
            await initIndexedDB();
            return await import('idb-keyval').then((m) => m.clear(store));
        },

        /**
         * ✨ 追加单条：key存的是数组，读取后push新元素再保存
         * @param key 存储的key
         * @param item 要追加的对象/数据
         */
        async appendItem<T = any>(key: string, item: T) {
            await initIndexedDB();
            const list: T[] = await this.getItem(key) || [];
            list.push(item);
            return this.setItem(key, list);
        },

        /**
         * ✨ 批量追加：一次性追加多条数组
         * @param key 存储的key
         * @param items 要追加的数组
         */
        async appendBatch<T = any>(key: string, items: T[]) {
            await initIndexedDB();
            if (!Array.isArray(items)) {
                throw new Error('appendBatch 第二个参数必须是数组');
            }
            const list: T[] = await this.getItem(key) || [];
            list.push(...items);
            return this.setItem(key, list);
        }
    };
}

// 实例
export const paramDB = getDB('param-store');
export const columnsDB = getDB('columns-store');
export const screenDB = getDB('screen-store');
export const temporaryDB = getDB('temporary-store');
export const pluginDB = getDB('plugin-store');